import { useDebounce } from "@/app/hooks/useDebounce";
import { cltService } from "@/app/services/cltService";
import { ITableClt } from "@/app/services/cltService/getTables";
import { customerService } from "@/app/services/customerService";
import masks from "@/app/utils/masks";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

export default function useAvailableOffersClt() {
  const { cpf } = useParams();
  const [selectedTable, setSelectedTable] = useState<ITableClt | null>(null);
  const [financedValue, setFinancedValue] = useState<number | undefined>(
    undefined,
  );

  const debouncedValue = useDebounce(1500, financedValue?.toString());

  const {
    data: availableOffers,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["availableOffers", cpf, debouncedValue],
    queryFn: () =>
      cltService.getTables({
        cpf: masks.cpf(cpf),
        valorFinanciado: debouncedValue ? Number(debouncedValue) : undefined,
      }),
    enabled: !!cpf && debouncedValue !== "0",
  });

  const isLoadingAvailableOffers = isLoading && !financedValue;
  const isLoadingFinancedValue = isLoading && !!financedValue;
  const isErrorAvailableOffers = !!error && !financedValue;
  const isErrorFinancedValue = !!error && !!financedValue;

  const { data: customer, isLoading: customerIsLoading } = useQuery({
    queryKey: ["getCustomer", cpf],
    queryFn: () => (cpf ? customerService.customerInfo(cpf) : undefined),
  });

  useEffect(() => {
    if (availableOffers && availableOffers.tables?.length > 0) {
      setSelectedTable(availableOffers.tables[0] as ITableClt);
    }
  }, [availableOffers]);

  const installments = useMemo(() => {
    const prazos = availableOffers?.prazos || [];
    if (prazos.length > 0) {
      return prazos.map((quota) => ({
        value: quota,
        label: quota,
      }));
    }
    return selectedTable
      ? [{ value: selectedTable.Prazo, label: selectedTable.Prazo }]
      : [];
  }, [availableOffers, selectedTable]);

  const handleRefresh = () => {
    setFinancedValue(undefined);
    setSelectedTable(null);
    refetch();
  };

  const handleSelectInstallments = (value: number) => {
    const tableWithSelectedPrazo = availableOffers?.tables?.find(
      (table) => table.Prazo === value,
    );
    if (tableWithSelectedPrazo) {
      setSelectedTable(tableWithSelectedPrazo);
    }
  };

  return {
    customer,
    customerIsLoading,
    cpf,
    list: availableOffers?.tables || [],
    isLoading,
    error,
    selectedTable,
    setSelectedTable,
    installments,
    handleSelectInstallments,
    matricula: availableOffers?.matricula ?? 0,
    cnpjEmpresa: availableOffers?.cnpjEmpresa ?? 0,
    financedValue,
    setFinancedValue,
    isLoadingAvailableOffers,
    isLoadingFinancedValue,
    isErrorAvailableOffers,
    isErrorFinancedValue,
    handleRefresh,
  };
}
